import {AuthService} from "./AuthService";

// Admin-only, read-only client for the in-house unblocker's proxy-pool monitor. Delegates to
// the shared AuthService axios layer (Token auth per-request); the backend proxies the
// unblocker's /v1/pool so its token never reaches the panel. Mirrors CanaryService conventions.
export class ProxyMonitorService {

    url = `admin/proxy_monitor/`;

    // GET admin/proxy_monitor/ -> {summary:{total,enabled,disabled,groups[]}, proxies:[...]}
    getPool() {
        return AuthService.apiGet(this.url).then(res => res.data.data);
    }

    // GET admin/proxy_monitor/stats/?days=N -> {summary:{...}, rows:[{user,ip,host,source,type,
    // country,attempts,success,ban,error,timeout,latency_ms_sum}]} (infra-side request metrics)
    getStats(days = 7) {
        return AuthService.apiGet(`${this.url}stats/?days=${days}`).then(res => res.data.data);
    }

    // GET admin/proxy_monitor/store/ -> {providers:[{provider,balance,currency,products:[...],error}]}
    // Read-only provider catalog (products / prices / balance) for buy planning.
    getStore() {
        return AuthService.apiGet(`${this.url}store/`).then(res => res.data.data);
    }

    // GET admin/proxy_monitor/quote/?type&country&period&quantity -> {price,total,discount,currency,
    // balance,warning} (proxy-seller order/calc — read-only price quote, no purchase).
    getQuote({type, country, period, quantity}) {
        const params = new URLSearchParams({type, country, period, quantity});
        return AuthService.apiGet(`${this.url}quote/?${params.toString()}`).then(res => res.data.data);
    }

    // POST admin/proxy_monitor/set_enabled/ {ips:[...], enabled} -> {updated:N}
    // Manually enable (fresh start; still subject to the health-check) / disable pool proxies.
    setProxies(ips, enabled) {
        return AuthService.apiPost(`${this.url}set_enabled/`, {ips, enabled}).then(res => res.data.data);
    }

    // POST admin/proxy_monitor/set_scope/ {ips:[...], list_name, op:'add'|'remove', sites:[...]}
    // -> {updated:N}. list_name: capabilities | only_sites | deny_sites. For capabilities `sites`
    // carries tags; otherwise hosts. Single or bulk.
    setScope(ips, listName, op, sites) {
        return AuthService.apiPost(`${this.url}set_scope/`,
            {ips, list_name: listName, op, sites}).then(res => res.data.data);
    }

    // GET admin/proxy_monitor/site_rules/ -> {rules:[{host, requires:[...]}]}
    getSiteRules() {
        return AuthService.apiGet(`${this.url}site_rules/`).then(res => res.data.data);
    }

    // POST admin/proxy_monitor/set_site_rule/ {host, requires:[...]} -> {updated:N}
    // Set a site's required capabilities; empty requires reopens the site.
    setSiteRule(host, requires, requiresRender = false, maxConcurrency = null) {
        return AuthService.apiPost(`${this.url}set_site_rule/`,
            {host, requires, requires_render: requiresRender,
             max_concurrency: maxConcurrency}).then(res => res.data.data);
    }

    // GET admin/proxy_monitor/selection/ -> {config:[{key,value,description}], hosts:{host:[{ip,...}]}}
    // Adaptive success-weighted selection config + the current per-host weight table.
    getSelection() {
        return AuthService.apiGet(`${this.url}selection/`).then(res => res.data.data);
    }

    // POST admin/proxy_monitor/set_selection_config/ {key, value} -> {updated:N}
    // Set one selection_config key (e.g. enabled_hosts:[...], enabled:true). Unknown keys rejected.
    setSelectionConfig(key, value) {
        return AuthService.apiPost(`${this.url}set_selection_config/`, {key, value}).then(res => res.data.data);
    }

    // POST admin/proxy_monitor/probe/ {ips, url, capability, expect} ->
    // {results:[{ip,status,ok,blocked}], tagged:[ip], ok, total}. Probes the proxies against
    // the URL and tags the passers with `capability`. Long-running (server-side network).
    probe({ips, url, capability, expect}) {
        return AuthService.apiPost(`${this.url}probe/`, {ips, url, capability, expect},
            {timeout: 180000}).then(res => res.data.data);
    }

    // POST admin/proxy_monitor/sync/ -> {added, disabled, updated, synced[]}
    // Run the provider re-sync now (adds newly-bought IPs). Long timeout (server-side probes).
    syncNow() {
        return AuthService.apiPost(`${this.url}sync/`, {}, {timeout: 180000}).then(res => res.data.data);
    }

    // GET admin/proxy_metrics/?from=&to=&group_by=&granularity=&canary=... ->
    // {rows, summary, dimensions, from, to, group_by, granularity, canary}.
    // Scraper-side all-traffic efficiency metrics (price.proxy_metrics) — served by
    // pricerest from Mongo, NOT proxied to the unblocker like the calls above.
    getEfficiency(params) {
        const qs = new URLSearchParams(params).toString();
        return AuthService.apiGet(`admin/proxy_metrics/?${qs}`).then(res => res.data.data);
    }

    // GET admin/scraper_config/<key>/ -> {value, stored, default, options, updated_at,
    // updated_by}. `stored` is null while the scraper still runs on its own default -
    // the search provider class's `proxy_default`, or SearchProviderRegistry's
    // DEFAULT_ENGINE, not a settings.py constant. Keys: google_proxy, search_engine,
    // bing_proxy, yahoo_proxy, yandex_proxy.
    getScraperConfig(key) {
        return AuthService.apiGet(`admin/scraper_config/${key}/`).then(res => res.data.data);
    }

    // GET scraper_config/effective_search_engine/ -> {value}. The ONLY non-admin-readable
    // config action, deliberately registered under a non-admin prefix: the campaign search
    // dialog is a customer screen and has to preselect the engine that campaign will run on.
    getEffectiveSearchEngine() {
        return AuthService.apiGet('scraper_config/effective_search_engine/')
            .then(res => res.data.data?.value);
    }

    // POST admin/scraper_config/set_<key>/ {value} -> the same shape as getScraperConfig.
    // Takes effect on the NEXT scraping job; running jobs keep what they started with.
    setScraperConfig(key, value) {
        return AuthService.apiPost(`admin/scraper_config/set_${key}/`, {value})
            .then(res => res.data.data);
    }
}
